import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router-dom';

import './App.css';
import SupportWidget from './components/SupportWidget';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import CookieNotice from './components/CookieNotice';
import { SearchHistoryProvider } from './context/SearchHistoryContext';
import { DashboardHistoryProvider } from './context/DashboardHistoryContext';
import { ChatSessionsProvider } from './context/ChatSessionsContext';
import { LandingPage } from './features/auth/pages/LandingPage';
import { HomePage } from './features/marketing/HomePage';
import { Home1Page } from './features/marketing/Home1Page';
import { Home2Page } from './features/marketing/Home2Page';
import { LiveUpdatesPage } from './features/auth/pages/LiveUpdatesPage';
import { AuthPortalPage } from './features/auth/pages/AuthPortalPage';
import { ProtectedRoute } from './features/auth/components/ProtectedRoute';
import { ChatPage } from './features/chat/ChatPage';
import { AppLayout } from './features/layout/AppLayout';
import { TourProvider } from './features/tour/GuidedTour';
import MattersPage from './features/lawfirm/MattersPage';
import EarlyWarningPage from './features/earlyWarning/EarlyWarningPage';
import { NotFoundPage } from './features/misc/NotFoundPage';
import { OnboardingPage } from './features/onboarding/OnboardingPage';
import AgentsPage from './features/agents/AgentsPage';
import BrandingPage from './features/branding/BrandingPage';
import PolicyGeneratorPage from './features/policy/PolicyGeneratorPage';
import MatterDetailPage from './features/lawfirm/MatterDetailPage';
import FeatureGate from './features/billing/components/FeatureGate';
import { ProfilePage } from './features/profile/ProfilePage';
import { ChangePasswordPage } from './features/profile/ChangePasswordPage';
import { ChatHistoryPage } from './features/history/ChatHistoryPage';
import MySupportPage from './features/support/MySupportPage';
import DashboardPage from './features/dashboard/DashboardPage';
import RegulationsBreakdownPage from './features/dashboard/RegulationsBreakdownPage';
import SearchRegulationDetailPage from './features/regulations/pages/RegulationDetailPage';
import { FAQPage } from './features/info/pages/FAQPage';
import { AboutPage } from './features/info/pages/AboutPage';
import { SearchPage } from './features/search/SearchPage';
import { SearchHistoryPage } from './features/search/SearchHistoryPage';
import SearchResultsPage from './features/search/pages/SearchResultsPage';
import BillingPage from './features/billing/pages/BillingPage';
import PricingPage from './features/billing/pages/PricingPage';
import CheckoutPage from './features/billing/pages/CheckoutPage';
import TrialJourneyPage from './features/billing/pages/TrialJourneyPage';
import PaymentHistoryPage from './features/billing/pages/PaymentHistoryPage';
import { BillingProvider } from './context/BillingContext';
import { BrandingProvider } from './context/BrandingContext';
import AdminLoginPage from './features/admin/pages/AdminLoginPage';
import AdminDashboardPage from './features/admin/pages/AdminDashboardPage';
import AdminSecurityPage from './features/admin/pages/AdminSecurityPage';
import AdminSearchConfigPage from './features/admin/pages/AdminSearchConfigPage';
import AdminPlanMatrixPage from './features/admin/pages/AdminPlanMatrixPage';
import AdminBrandingPage from './features/admin/pages/AdminBrandingPage';
import AdminCreditsPage from './features/admin/pages/AdminCreditsPage';
import AdminDataQualityPage from './features/admin/pages/AdminDataQualityPage';
import AdminAnnouncementsPage from './features/admin/pages/AdminAnnouncementsPage';
import AdminInvoicesPage from './features/admin/pages/AdminInvoicesPage';
import AdminReportsPage from './features/admin/pages/AdminReportsPage';
import AdminContractsPage from './features/admin/pages/AdminContractsPage';
import AdminMasterDataPage from './features/admin/pages/AdminMasterDataPage';
import AdminExternalContentPage from './features/admin/pages/AdminExternalContentPage';
import AdminProvenancePage from './features/admin/pages/AdminProvenancePage';
import AdminFaqPage from './features/admin/pages/AdminFaqPage';
import AdminNewsletterPage from './features/admin/pages/AdminNewsletterPage';
import AdminFeedbackPage from './features/admin/pages/AdminFeedbackPage';
import NotificationsPage from './features/notifications/NotificationsPage';
import IntegrationsPage from './features/integrations/IntegrationsPage';
import UsersManagePage from './features/admin/pages/UsersManagePage';
import AddEnterpriseUserPage from './features/admin/pages/AddEnterpriseUserPage';
import DocumentsActivityPage from './features/admin/pages/DocumentsActivityPage';
import GeneratedTranscriptsPage from './features/admin/pages/GeneratedTranscriptsPage';
import TranscriptViewerPage from './features/admin/pages/TranscriptViewerPage';
import SyncedDocsPage from './features/admin/pages/SyncedDocsPage';
import DataSyncStatusPage from './features/admin/pages/DataSyncStatusPage';
import AddRegulationsPage from './features/admin/pages/AddRegulationsPage';
import AdminPricingPage from './features/admin/pages/AdminPricingPage';
import AdminPlansPage from './features/admin/pages/AdminPlansPage';
import AdminNewsCmsPage from './features/admin/pages/AdminNewsCmsPage';
import AdminAlertsPage from './features/admin/pages/AdminAlertsPage';
import { ComparisonPage } from './features/comparison/ComparisonPage';
import AlertsPage from './features/alerts/AlertsPage';
import { EmailVerificationPage } from './features/auth/pages/EmailVerificationPage';
import { SsoCompletePage } from './features/auth/pages/SsoCompletePage';
import { DeleteAccountConfirmationPage } from './features/auth/pages/DeleteAccountConfirmationPage';
import { GoogleCallbackPage } from './features/auth/pages/GoogleCallbackPage';
import CompanyProfilePage from './features/grc/pages/CompanyProfilePage';
import AssetRepositoryPage from './features/grc/pages/AssetRepositoryPage';
import GovernanceStartPage from './features/grc/pages/GovernanceStartPage';
import RiskStartPage from './features/grc/pages/RiskStartPage';
import ComplianceStartPage from './features/grc/pages/ComplianceStartPage';
import EnterpriseOnboardingPage from './features/enterprise/pages/EnterpriseOnboardingPage';
import EnterpriseDashboardPage from './features/enterprise/pages/EnterpriseDashboardPage';
import TeamFoldersPage from './features/collaboration/TeamFoldersPage';
import EnterpriseSearchPage from './features/enterprise/pages/EnterpriseSearchPage';
import EnterpriseChatPage from './features/enterprise/pages/EnterpriseChatPage';
import EnterpriseProfileEditPage from './features/enterprise/pages/EnterpriseProfileEditPage';
import { EnterpriseRoute } from './features/enterprise/components/EnterpriseRoute';
import { GovernanceProvider } from './context/GovernanceContext';
import { RiskProvider } from './context/RiskContext';
import { ComplianceProvider } from './context/ComplianceContext';
import EnterpriseRegulationsPage from './features/enterprise/pages/EnterpriseRegulationsPage';
import EnterpriseNewslettersPage from './features/enterprise/pages/EnterpriseNewslettersPage';
import EnterpriseChartsPage from './features/enterprise/pages/EnterpriseChartsPage';
import EnterpriseMapPage from './features/enterprise/pages/EnterpriseMapPage';
import EnterpriseTeamPage from './features/enterprise/pages/EnterpriseTeamPage';
import EnterpriseUserGuidePage from './features/enterprise/pages/EnterpriseUserGuidePage';
import EnterpriseChatHistoryPage from './features/enterprise/pages/EnterpriseChatHistoryPage';
import EnterpriseSearchHistoryPage from './features/enterprise/pages/EnterpriseSearchHistoryPage';
import EnterpriseDeleteAccountPage from './features/enterprise/pages/EnterpriseDeleteAccountPage';
import StartupDashboardPage from './features/startup/pages/StartupDashboardPage';
import GovernmentDashboardPage from './features/government/pages/GovernmentDashboardPage';

// Redirect /regulation/:id → /regulations/:id (full detail page)
const NavigateToRegulation: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/regulations/${id}`} replace />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BillingProvider>
        <BrandingProvider>
        <SearchHistoryProvider>
          <DashboardHistoryProvider>
            <ChatSessionsProvider>
            <GovernanceProvider>
            <RiskProvider>
            <ComplianceProvider>
            <BrowserRouter>
          <TourProvider>{/* SRS #39 */}
          <Routes>
            {/* KN designed this one themselves and approved it, so it is the
                site's front door (SRS #56). It stays reachable at "/home2" as
                well, because that is the URL they have been reviewing and
                sharing. */}
            <Route path="/" element={<Home2Page />} />
            <Route path="/home2" element={<Home2Page />} />
            {/* The three earlier designs are kept rather than deleted, so any
                of them can be gone back to without a release:
                  /home   our first proposal — dark, aurora, live map and chart
                  /home1  our second — light and colour-blocked
                  /home3  the original landing page that used to be on "/" */}
            <Route path="/home" element={<HomePage />} />
            <Route path="/home1" element={<Home1Page />} />
            <Route path="/home3" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/login" element={<AuthPortalPage initialMode="login" />} />
            <Route path="/signup" element={<AuthPortalPage initialMode="register" />} />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/auth/sso/complete" element={<SsoCompletePage />} />
            <Route path="/confirm-delete-account" element={<DeleteAccountConfirmationPage />} />
            <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
            <Route path="/live-updates" element={<LiveUpdatesPage />} />
            <Route path="/faq" element={<FAQPage />} />
            {/* News is withdrawn at the client's request: every /news address goes to the home page,
                for visitors and signed-in customers alike. The news pages and the admin editor stay in
                the code; only the public routes are removed. */}
            <Route path="/news" element={<Navigate to="/" replace />} />
            <Route path="/news/*" element={<Navigate to="/" replace />} />
            
           
            {/* Where a customer chooses their operating jurisdictions, industry and
                use cases. The page existed and was never routed: Profile's Edit
                Profile button sent you to /onboarding, which matched nothing, so
                nobody could set a selection — and with no selection the plan's
                jurisdiction cap has nothing to apply to, which is why every
                account saw every jurisdiction whatever it had paid for. */}
            <Route
              path="/onboarding"
              element={(
                <ProtectedRoute>
                  <OnboardingPage />
                </ProtectedRoute>
              )}
            />

            {/* Enterprise Routes */}
            <Route
              path="/enterprise/onboarding"
              element={
                <EnterpriseRoute requireOnboarding={false}>
                  <EnterpriseOnboardingPage />
                </EnterpriseRoute>
              }
            />
            <Route
              path="/enterprise/profile/edit"
              element={
                <EnterpriseRoute>
                  <EnterpriseProfileEditPage />
                </EnterpriseRoute>
              }
            />

            {/* Startup Routes */}
            <Route
              path="/startup/dashboard"
              element={(
                <ProtectedRoute>
                  <StartupDashboardPage />
                </ProtectedRoute>
              )}
            >
              <Route path="search" element={<EnterpriseSearchPage />} />
              <Route path="chat" element={<EnterpriseChatPage />} />
              <Route path="chat-history" element={<EnterpriseChatHistoryPage />} />
              <Route path="search-history" element={<EnterpriseSearchHistoryPage />} />
              <Route path="regulations" element={<EnterpriseRegulationsPage />} />
              <Route path="newsletters" element={<EnterpriseNewslettersPage />} />
              <Route path="charts" element={<EnterpriseChartsPage />} />
              <Route path="regulations-breakdown" element={<RegulationsBreakdownPage />} />
              <Route path="map" element={<EnterpriseMapPage />} />
              <Route path="account" element={<ProfilePage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="guide" element={<EnterpriseUserGuidePage />} />  {/* SRS #42 */}
            </Route>

            {/* Government Routes */}
            <Route
              path="/government/dashboard"
              element={(
                <ProtectedRoute>
                  <GovernmentDashboardPage />
                </ProtectedRoute>
              )}
            >
              <Route path="search" element={<EnterpriseSearchPage />} />
              <Route path="chat" element={<EnterpriseChatPage />} />
              <Route path="chat-history" element={<EnterpriseChatHistoryPage />} />
              <Route path="search-history" element={<EnterpriseSearchHistoryPage />} />
              <Route path="regulations" element={<EnterpriseRegulationsPage />} />
              <Route path="newsletters" element={<EnterpriseNewslettersPage />} />
              <Route path="charts" element={<EnterpriseChartsPage />} />
              <Route path="regulations-breakdown" element={<RegulationsBreakdownPage />} />
              <Route path="map" element={<EnterpriseMapPage />} />
              <Route path="account" element={<ProfilePage />} />
              <Route path="billing" element={<BillingPage />} />
              <Route path="guide" element={<EnterpriseUserGuidePage />} />  {/* SRS #42 */}
            </Route>

            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/dashboard/users/manage" element={<UsersManagePage />} />
            <Route path="/admin/dashboard/users/add-enterprise" element={<AddEnterpriseUserPage />} />
            <Route path="/admin/dashboard/:section" element={<AdminDashboardPage />} />
            <Route path="/admin/documents/activity" element={<DocumentsActivityPage />} />
            <Route path="/admin/dashboard/generated-transcripts" element={<GeneratedTranscriptsPage />} />
            <Route path="/admin/dashboard/generated-transcripts/view" element={<TranscriptViewerPage />} />
            <Route path="/admin/dashboard/synced-docs" element={<SyncedDocsPage />} />
            <Route path="/admin/dashboard/data-sync" element={<DataSyncStatusPage />} />
            <Route path="/admin/dashboard/add-regulations" element={<AddRegulationsPage />} />
            <Route path="/admin/dashboard/pricing" element={<AdminPlansPage />} />
            <Route path="/admin/dashboard/pricing/legacy" element={<AdminPricingPage />} />
            <Route path="/admin/dashboard/news-cms" element={<AdminNewsCmsPage />} />
            <Route path="/admin/dashboard/alert-matrix" element={<AdminAlertsPage />} />
            <Route path="/admin/dashboard/security" element={<AdminSecurityPage />} />
            <Route path="/admin/dashboard/search-config" element={<AdminSearchConfigPage />} />
            <Route path="/admin/dashboard/plan-matrix" element={<AdminPlanMatrixPage />} />
            <Route path="/admin/dashboard/credits" element={<AdminCreditsPage />} />
            <Route path="/admin/dashboard/branding" element={<AdminBrandingPage />} />  {/* SRS #102 */}
            <Route path="/admin/dashboard/data-quality" element={<AdminDataQualityPage />} />
            <Route path="/admin/dashboard/announcements" element={<AdminAnnouncementsPage />} />
            <Route path="/admin/dashboard/invoices" element={<AdminInvoicesPage />} />
            <Route path="/admin/dashboard/reports" element={<AdminReportsPage />} />
            <Route path="/admin/dashboard/contracts" element={<AdminContractsPage />} />
            <Route path="/admin/dashboard/master-data" element={<AdminMasterDataPage />} />
            <Route path="/admin/dashboard/feedback" element={<AdminFeedbackPage />} />
            <Route path="/admin/dashboard/newsletter" element={<AdminNewsletterPage />} />
            <Route path="/admin/dashboard/faq" element={<AdminFaqPage />} />
            <Route path="/admin/dashboard/provenance" element={<AdminProvenancePage />} />
            <Route path="/admin/dashboard/external-content" element={<AdminExternalContentPage />} />

            {/* Regulation detail - redirect singular to plural (full detail page) */}
            <Route
              path="/regulation/:id"
              element={<NavigateToRegulation />}
            />

            {/* Checkout stays outside the app shell on purpose: a payment flow
                is better without a menu to wander off into. It is reached from
                signup with a plan chosen, from e-mail verification, and from
                every upgrade prompt in chat. */}
            <Route
              path="/billing/checkout"
              element={(
                <ProtectedRoute>
                  <CheckoutPage />
                </ProtectedRoute>
              )}
            />

            <Route
              path="/comparison"
              element={(
                <EnterpriseRoute>
                  <Navigate to="/enterprise/dashboard/comparison" replace />
                </EnterpriseRoute>
              )}
            />

            {/* Routes with AppLayout (with sidebar) - ProtectedRoute wraps AppLayout so it only
                 renders after auth resolves, preventing race conditions with null user */}
            <Route
              element={(
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              )}
            >
              <Route path="/app" element={<ChatPage />} />
            <Route
              path="/enterprise/dashboard"
              element={
                <EnterpriseRoute>
                  <EnterpriseDashboardPage />
                </EnterpriseRoute>
              }
            >
              <Route path="search" element={<EnterpriseSearchPage />} />
              <Route path="chat" element={<EnterpriseChatPage />} />
              <Route path="chat-history" element={<EnterpriseChatHistoryPage />} />
              <Route path="search-history" element={<EnterpriseSearchHistoryPage />} />
              <Route path="company-profile" element={<CompanyProfilePage />} />
              <Route path="repository" element={<AssetRepositoryPage />} />
              <Route path="governance" element={<GovernanceStartPage />} />
              <Route path="risk" element={<RiskStartPage />} />
              <Route path="compliance" element={<ComplianceStartPage />} />
              <Route path="comparison" element={<ComparisonPage />} />
              <Route path="regulations" element={<EnterpriseRegulationsPage />} />
              <Route path="newsletters" element={<EnterpriseNewslettersPage />} />
              <Route path="charts" element={<EnterpriseChartsPage />} />
              <Route path="regulations-breakdown" element={<RegulationsBreakdownPage />} />
              <Route path="map" element={<EnterpriseMapPage />} />
              <Route path="team" element={<EnterpriseTeamPage />} />
              <Route path="folders" element={<TeamFoldersPage />} />
              <Route path="guide" element={<EnterpriseUserGuidePage />} />
              <Route path="delete-account" element={<EnterpriseDeleteAccountPage />} />
            </Route>
              <Route path="/alerts" element={<FeatureGate feature="custom_alerts" fallbackTitle="Custom alerts are not included in your plan"><AlertsPage /></FeatureGate>} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/support" element={<MySupportPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/integrations" element={<IntegrationsPage />} />
              <Route path="/settings/branding" element={<BrandingPage />} />  {/* SRS #38 white-labelling */}
              <Route path="/team" element={<EnterpriseTeamPage />} />
              <Route path="/change-password" element={<ChangePasswordPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/history" element={<ChatHistoryPage />} />
              <Route path="/grc/company-profile" element={<CompanyProfilePage />} />
              <Route path="/grc/repository" element={<AssetRepositoryPage />} />
              <Route path="/governance/start" element={<FeatureGate feature="advanced_ai" fallbackTitle="AI governance assessments require an upgrade"><GovernanceStartPage /></FeatureGate>} />
              <Route path="/governance" element={<FeatureGate feature="advanced_ai" fallbackTitle="AI governance assessments require an upgrade"><GovernanceStartPage /></FeatureGate>} />
              <Route path="/governance/:sessionId" element={<FeatureGate feature="advanced_ai" fallbackTitle="AI governance assessments require an upgrade"><GovernanceStartPage /></FeatureGate>} />
              <Route path="/risk/start" element={<FeatureGate feature="advanced_ai" fallbackTitle="AI risk assessments require an upgrade"><RiskStartPage /></FeatureGate>} />
              <Route path="/risk" element={<FeatureGate feature="advanced_ai" fallbackTitle="AI risk assessments require an upgrade"><RiskStartPage /></FeatureGate>} />
              <Route path="/risk/:sessionId" element={<FeatureGate feature="advanced_ai" fallbackTitle="AI risk assessments require an upgrade"><RiskStartPage /></FeatureGate>} />
              <Route path="/compliance/start" element={<FeatureGate feature="advanced_ai" fallbackTitle="AI compliance assessments require an upgrade"><ComplianceStartPage /></FeatureGate>} />
              <Route path="/compliance" element={<FeatureGate feature="advanced_ai" fallbackTitle="AI compliance assessments require an upgrade"><ComplianceStartPage /></FeatureGate>} />
              <Route path="/compliance/:sessionId" element={<FeatureGate feature="advanced_ai" fallbackTitle="AI compliance assessments require an upgrade"><ComplianceStartPage /></FeatureGate>} />
              <Route path="/dashboard/regulations-breakdown" element={<RegulationsBreakdownPage />} />
              <Route path="/dashboard/*" element={<DashboardPage />} />
              {/* Signed in means the menu is there. These sat outside the shell
                  so Search, Billing and the regulation detail had no left panel
                  at all, and the customer lost the menu by opening one of them. */}
              <Route path="/search" element={<SearchPage />} />
              <Route path="/search/results" element={<SearchResultsPage />} />
              <Route path="/search-history" element={<SearchHistoryPage />} />
              <Route path="/regulations/:id" element={<SearchRegulationDetailPage />} />
              <Route path="/billing/payment-history" element={<PaymentHistoryPage />} />
              <Route path="/billing/trial-journey" element={<TrialJourneyPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/guide" element={<EnterpriseUserGuidePage />} />  {/* SRS #42: user guide on every plan */}
              <Route path="/matters" element={<MattersPage />} />  {/* SRS #49/#50 */}
              <Route path="/early-warning" element={<EarlyWarningPage />} />  {/* SRS #60 */}
              <Route path="/agents" element={<AgentsPage />} />  {/* SRS #61 */}
              <Route path="/agents/:id" element={<AgentsPage />} />
              <Route path="/policy" element={<PolicyGeneratorPage />} />  {/* SRS #152–#157 */}
              <Route path="/matters/:id" element={<MatterDetailPage />} />
            </Route>
            {/* Anything else: a stale link or a typed address used to render a
                blank white page, because no route matched and none caught it. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <SupportWidget />
          <CookieNotice />
          </TourProvider>
        </BrowserRouter>
            </ComplianceProvider>
            </RiskProvider>
            </GovernanceProvider>
            </ChatSessionsProvider>
          </DashboardHistoryProvider>
        </SearchHistoryProvider>
        </BrandingProvider>
        </BillingProvider>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;
